import { useCallback } from 'react';
import Container from '@ui/container/container';
import WatermarkDurationSelector from './controls/watermark-duration-selector';
import WatermarkPctgInput from './controls/watermark-pctg-input';
import WatermarkAxisInput from './controls/watermark-axis-input';
import WatermarkRotateInput from './controls/watermark-rotate-input';
import Checkbox from '@ui/checkbox/checkbox';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import type { IWatermarkElement } from '@/types/watermarks.types';

type Settings = IWatermarkElement['settings'];

interface AddWatermarkEmailBlockProps {
  settings: Settings;
  onSettingsChange: (settings: Settings) => void;
}

const AddWatermarkEmailBlock = ({ settings, onSettingsChange }: AddWatermarkEmailBlockProps) => {
  const dimensions = settings.dimensions;

  const handleUpdate = useCallback(
    <K extends keyof Settings>(key: K, value: Settings[K]) => {
      onSettingsChange({ ...settings, [key]: value });
    },
    [settings, onSettingsChange]
  );

  return (
    <Container className="flex-column gap-16">
      <Container className="flex-column gap-8">
        <Container className="flex gap-8 ai-flex-end">
          <WatermarkPctgInput
            label="Opacity"
            min={0}
            max={100}
            value={String(Math.round((settings.opacity ?? 1) * 100))}
            onChange={(val) => {
              const n = parseFloat(val);
              if (!isNaN(n)) handleUpdate('opacity', n / 100);
            }}
          />
          {/* Leaving the following in place in case product decides to add it back */}
          {/* <WatermarkFontSelector
            value={settings.text.font.name}
            onChange={(val) => handleUpdate('text', { ...settings.text, font: { ...settings.text.font, name: val } })}
          /> */}
          <WatermarkPctgInput
            label="Scale"
            value={String(Math.round(settings.dimensions.scale * 100))}
            onChange={(val) => {
              const n = parseFloat(val);
              if (!isNaN(n)) handleUpdate('dimensions', { ...dimensions, scale: n / 100 });
            }}
          />
        </Container>

        <Container className="flex gap-8">
          <WatermarkAxisInput
            label="Position"
            prefix="X"
            value={isNaN(settings.dimensions.x) ? '' : settings.dimensions.x.toString()}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, x: parseFloat(val) })}
            disabled={settings.repeat}
          />
          <WatermarkAxisInput
            prefix="Y"
            value={isNaN(settings.dimensions.y) ? '' : settings.dimensions.y.toString()}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, y: parseFloat(val) })}
            disabled={settings.repeat}
          />
          <WatermarkRotateInput
            label="Angle"
            value={String(settings.dimensions.rotation ?? 0)}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, rotation: parseFloat(val) || 0 })}
          />
        </Container>

        <Checkbox
          label={
            <Text className="L2" design="GlobalIcoText3">
              Repeat as pattern
            </Text>
          }
          color="var(--global-ico-text-3)"
          className="S2 flex gap-6"
          checked={settings.repeat}
          value="repeat"
          onChange={(_val, isOn) => handleUpdate('repeat', isOn)}
          selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
          unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
        />
      </Container>

      <WatermarkDurationSelector value={settings.duration} onChange={(val) => handleUpdate('duration', val)} />
    </Container>
  );
};

export default AddWatermarkEmailBlock;
